import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {getBuildingsGraph} from '../actions/buildingsActions';
import ElevatorsTable from '../components/CustomTables/ElevatorsTable';
import {Filters, Layout, PageHeader} from '../components';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import {Typography, Box, Pagination, Card, CardContent} from '@mui/material';
import {getElevators, getPaymentsGraph} from '../actions';
import useFilters from '../utils/useFilters';
import {getOverusedCards} from '../actions/cardActions';
import OverUsedCardsTable from '../components/CustomTables/OverUsedCardsTable';
import {getInactiveElevators} from '../actions/elevatorsActions';
import InactiveElevatorsTable from '../components/CustomTables/InactiveElevatorsTable';

const Dashboard = () => {
  const dispatch = useDispatch();
  const {
    overUsedCards,
    overUsedCardsLoading,
    overUsedCardsPages,
    overUsedCardsTotalPages,
  } = useSelector(s => s.cards);
  const {
    inactiveElevators,
    inactiveElevatorsLoading,
    inactiveElevatorsPage,
    inactiveElevatorsTotalPages,
  } = useSelector(s => s.elevators);
  const filters1 = useFilters(val => dispatch(getOverusedCards(val)));
  const filters2 = useFilters(val => dispatch(getInactiveElevators(val)));

  const onPageChange1 = (e, value) => {
    dispatch(getOverusedCards({page: value, ...filters1.filters}));
  };

  const onPageChange2 = (e, value) => {
    dispatch(getInactiveElevators({page: value, ...filters2.filters}));
  };

  useEffect(() => {
    dispatch(getOverusedCards());
    dispatch(getInactiveElevators());
  }, []);

  const {buildingsGraph} = useSelector(s => s.buildings);
  const {paymentsGraph} = useSelector(s => s.payments);
  const [paymentsData, setPaymentsData] = useState([]);

  useEffect(() => {
    if (paymentsGraph.length > 0) {
      const data = [];
      for (let i in paymentsGraph) {
        data.push(...paymentsGraph[i].data);
      }
      setPaymentsData(data);
    } else {
      setPaymentsData([]);
    }
  }, [paymentsGraph]);

  useEffect(() => {
    dispatch(getBuildingsGraph());
    dispatch(getPaymentsGraph());
  }, []);

  // Mobile detection
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 720px)');
    const handleMediaQueryChange = e => setIsMobile(e.matches);

    setIsMobile(mediaQuery.matches);

    mediaQuery.addEventListener('change', handleMediaQueryChange);

    return () =>
      mediaQuery.removeEventListener('change', handleMediaQueryChange);
  }, [overUsedCards, inactiveElevators]);

  return (
    <Layout>
      <Box minHeight="100%" display="flex" flexDirection="column">
        {/* Graph: Top 10 Buildings with Most Customers */}
        {!isMobile && (
          <Typography variant="h6" mt="4rem" mb="1rem" mx="auto">
            Top 10 buildings with most costumers
          </Typography>
        )}
        <Box flex={1} minWidth={0} width="100%">
          {isMobile ? (
            // Mobile Card View for Graph
            <Card sx={{m: '1rem', overflow: 'hidden'}}>
              <CardContent sx={{px: 1}}>
                <Typography variant="h6" gutterBottom>
                  Top 10 buildings with most customers
                </Typography>
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart
                    data={buildingsGraph}
                    margin={{top: 5, right: 8, left: -16, bottom: 5}}>
                    <CartesianGrid strokeDasharray="3" />
                    <XAxis dataKey="building" tick={{fontSize: 10}} interval={0} tickFormatter={v => (v?.length > 8 ? `${v.slice(0, 8)}…` : v)} angle={-35} textAnchor="end" height={60} />
                    <YAxis tick={{fontSize: 10}} />
                    <Tooltip />
                    <Legend />
                    <Bar
                      dataKey="customersNr"
                      name="Customers number"
                      fill="#1976d2"
                    />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          ) : (
            // Desktop Graph View
            <ResponsiveContainer minHeight={300}>
              <BarChart
                data={buildingsGraph}
                margin={{top: 5, right: 30, left: 20, bottom: 5}}>
                <CartesianGrid strokeDasharray="3" />
                <XAxis dataKey="building" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar
                  dataKey="customersNr"
                  name="Customers number"
                  fill="#1976d2"
                />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Box>

        {/* Graph: Top 5 Profitable Buildings */}
        {!isMobile && (
          <Typography variant="h6" mt="2rem" mb="1rem" mx="auto">
            Top 5 profitable buildings in last 5 months
          </Typography>
        )}
        <Box flex={1} minWidth={0} width="100%">
          {isMobile ? (
            // Mobile Card View for Graph
            <Card sx={{m: '1rem', overflow: 'hidden'}}>
              <CardContent sx={{px: 1}}>
                <Typography variant="h6" gutterBottom>
                  Top 5 profitable buildings in last 5 months
                </Typography>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart
                    data={paymentsData}
                    margin={{top: 5, right: 8, left: -16, bottom: 5}}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="month"
                      xAxisId="month"
                      tick={{fontSize: 10}}
                      interval={0}
                      tickLine={false}
                      // one label per month group (bars are grouped by month, 3 buildings each)
                      tickFormatter={(v, i) =>
                        paymentsData[i - 1]?.month === v
                          ? ''
                          : String(v).replace(/^(\w{3})\w* (\d{2})(\d{2})$/, '$1 $3')
                      }
                    />
                    <XAxis dataKey="building" tick={{fontSize: 9}} interval={0} tickFormatter={v => (v?.length > 7 ? `${v.slice(0, 7)}…` : v)} angle={-35} textAnchor="end" height={50} />
                    <YAxis tick={{fontSize: 10}} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="sum" name="Profited (€)" fill="#1976d2" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          ) : (
            // Desktop Graph View
            <ResponsiveContainer minHeight={300}>
              <BarChart
                data={paymentsData}
                margin={{top: 5, right: 30, left: 20, bottom: 5}}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" xAxisId="month" />
                <XAxis dataKey="building" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="sum" name="Profited (€)" fill="#1976d2" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Box>

        {/* Cards and Elevators Tables */}
        {isMobile ? (
          <Typography variant="h6" mt="1.5rem" mb="0.5rem" px="1rem">
            Cards used more than 15 times a day
          </Typography>
        ) : (
          <Typography variant="h6" mt="2rem" mb="1rem" mx="auto">
            Cards used more than 15 times a day
          </Typography>
        )}
        <Filters withSearch {...filters1} m="1rem !important" />
        {!overUsedCardsLoading && overUsedCards.length === 0 ? (
          <Typography align="center" mt={2} color="textPrimary" style={{ fontWeight: 'bold' }}>
            No overused cards found.
          </Typography>
        ) : (
          <OverUsedCardsTable
            overUsedCards={overUsedCards}
            loading={overUsedCardsLoading}
            hideAddButton={true}
          />
        )}
        {overUsedCardsTotalPages > 1 && (
          <Box mt="1rem" display="flex" justifyContent="center">
            <Pagination
              count={overUsedCardsTotalPages}
              page={overUsedCardsPages}
              onChange={onPageChange1}
            />
          </Box>
        )}

        {isMobile ? (
          <Typography variant="h6" mt="1.5rem" mb="0.5rem" px="1rem">
            Elevators having no logs in last 24 hours
          </Typography>
        ) : (
          <Typography variant="h6" mt="2rem" mb="1rem" mx="auto">
            Elevators having no logs in last 24 hours
          </Typography>
        )}
        <Filters withSearch {...filters2} m="1rem !important" />
        {!inactiveElevatorsLoading && inactiveElevators.length === 0 ? (
          <Typography align="center" mt={2} color="textPrimary" style={{ fontWeight: 'bold' }}>
            No inactive elevators found.
          </Typography>
        ) : (
          <InactiveElevatorsTable
            inactiveElevators={inactiveElevators}
            loading={inactiveElevatorsLoading}
            hideAddButton={true}
          />
        )}
        {inactiveElevatorsTotalPages > 1 && (
          <Box mt="1rem" display="flex" justifyContent="center">
            <Pagination
              count={inactiveElevatorsTotalPages}
              page={inactiveElevatorsPage}
              onChange={onPageChange2}
            />
          </Box>
        )}
      </Box>
    </Layout>
  );
};

export default Dashboard;
